"use client";

import React, { useState, useEffect, useLayoutEffect, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { motion, AnimatePresence, type Variants } from "motion/react";
import { useAni } from "@/context/AniContext";
import { useIsOsMode, useWindowId } from "@/components/ui/os-layout";
import { OverlayContainmentContext } from "@/components/ui/dialog/Dialog";
import {
  getOsWindowContentElement,
  getOsWindowOverlayPortalElement,
} from "@/components/ui/os-layout/osOverlayPortal";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  width?: string;
  headerActions?: React.ReactNode;
  isExpanded?: boolean;
  onExpand?: () => void;
  onAnimationComplete?: () => void;
  position?: "right" | "left" | "top" | "bottom";
  responsiveWidth?: "full" | "screen-lg" | "screen-xl" | "screen-2xl";
  windowId?: string;
  "data-testid"?: string;
}

export default function Drawer({
  isOpen,
  onClose,
  title,
  children,
  width = "max-w-md",
  headerActions,
  isExpanded = false,
  onExpand,
  onAnimationComplete,
  position = "right",
  responsiveWidth = "screen-xl",
  windowId,
  "data-testid": testId,
}: DrawerProps) {
  const [mounted, setMounted] = useState(false);
  const [osPortalTarget, setOsPortalTarget] = useState<HTMLElement | null>(null);
  const { widgetViewState } = useAni();
  const isInOsMode = useIsOsMode();
  const currentWindowId = useWindowId();
  const targetWindowId = windowId || currentWindowId;
  const containmentTarget = useContext(OverlayContainmentContext);
  const isContained = Boolean(containmentTarget) && !isInOsMode;
  const useLocalOverlay = isInOsMode || isContained;
  const contentRef = useRef<HTMLDivElement>(null);
  
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true
  );
  
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const onMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };
    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", onMediaChange);
    return () => mediaQuery.removeEventListener("change", onMediaChange);
  }, []);
  
  const isAniDockedAndOpen = 
    widgetViewState.isOpen && 
    widgetViewState.mode === "docked" && 
    isDesktop;
  
  const rightOffset =
    !useLocalOverlay && isAniDockedAndOpen && position === "right" ? "440px" : "1rem";

  useEffect(() => {
    if (isOpen && isInOsMode && targetWindowId) {
      const windowContent = getOsWindowContentElement(targetWindowId);
      if (windowContent) {
        const originalOverflow = windowContent.style.overflow;
        windowContent.style.overflow = "hidden";

        return () => {
          windowContent.style.overflow = originalOverflow;
        };
      }
    }
  }, [isOpen, isInOsMode, targetWindowId]);

  useLayoutEffect(() => {
    if (!isInOsMode) {
      setOsPortalTarget(null);
      return;
    }

    if (!targetWindowId) {
      setOsPortalTarget(null);
      return;
    }

    setOsPortalTarget(getOsWindowOverlayPortalElement(targetWindowId));
  }, [isInOsMode, targetWindowId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const el = contentRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const id = requestAnimationFrame(() => {
      el.scrollTop = 0;
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, title]);

  const resetContentScroll = () => {
    const el = contentRef.current;
    if (el) el.scrollTop = 0;
  };

  const handleDrawerAnimationComplete = (definition: string) => {
    onAnimationComplete?.();
    if (definition === "visible") {
      resetContentScroll();
      window.setTimeout(resetContentScroll, 550);
    }
  };

  if (!mounted && !isOpen) return null;

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        delay: 0.2,
        duration: 0.2,
      },
    },
  };

  const getAnimationOffsetX = () => {
    if (position === "right" && isAniDockedAndOpen && !useLocalOverlay) {
      return 100;
    }
    if (position === "right") {
      return "100%";
    }
    if (position === "left") {
      return "-100%";
    }
    return 0;
  };

  const drawerVariants: Variants = {
    hidden: {
      x: getAnimationOffsetX(),
      y: position === "top" ? "-100%" : position === "bottom" ? "100%" : 0,
      opacity: 0,
    },
    visible: {
      x: 0,
      y: 0,
      opacity: 1,
      width:
        position === "left" || position === "right"
          ? "100%"
          : isExpanded
            ? "100%"
            : "auto",
      height:
        position === "top" || position === "bottom"
          ? isExpanded
            ? "100vh"
            : "calc(100vh - 2rem)"
          : "100%",
      transition: {
        type: "spring" as const,
        damping: 25,
        stiffness: 300,
        mass: 0.8,
      },
    },
    exit: {
      x: getAnimationOffsetX(),
      y: position === "top" ? "-100%" : position === "bottom" ? "100%" : 0,
      opacity: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300,
        mass: 0.8,
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        delay: 0.5,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.15,
      },
    },
  };

  // Shared drawer content structure
  const drawerContent = (
    <>
      {/* Backdrop */}
      <motion.div
        className={`${useLocalOverlay ? "absolute" : "fixed"} inset-0 ${isInOsMode ? "bg-slate-900/30" : "bg-slate-900/20 dark:bg-slate-700/70"} ${isInOsMode ? "z-[99998]" : "z-40"}`}
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        aria-hidden="true"
        onClick={onClose}
      />

      <div className={`${useLocalOverlay ? "absolute" : "fixed"} inset-0 overflow-hidden ${isInOsMode ? "z-[99999]" : "z-50"}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={`pointer-events-none ${useLocalOverlay ? "absolute" : "fixed"} ${
              position === "right"
                ? `inset-y-4 pl-10 ${useLocalOverlay ? "right-4" : "transition-[right] duration-300 ease-in-out"}`
                : position === "left"
                  ? "inset-y-4 left-4 pr-10"
                  : position === "top"
                    ? "top-4 inset-x-4 pb-10"
                    : "bottom-4 inset-x-4 pt-10"
            } flex min-h-0 ${
              position === "top" || position === "bottom"
                ? "w-full justify-center"
                : position === "right"
                  ? "h-full w-full items-stretch justify-end"
                  : position === "left"
                    ? "h-full w-full items-stretch justify-start"
                    : "h-full items-stretch"
            }`}
            style={
              position === "right" && !useLocalOverlay
                ? { right: rightOffset }
                : undefined
            }
          >
            <div
              className={`pointer-events-auto relative z-50 flex h-full min-h-0 max-h-full flex-col ${
                position === "left" || position === "right"
                  ? `h-full w-full shrink-0 ${!isExpanded ? width : ""}`
                  : `w-full ${
                      !isExpanded
                        ? responsiveWidth === "full"
                          ? "w-full"
                          : responsiveWidth === "screen-lg"
                            ? "max-w-screen-lg"
                            : responsiveWidth === "screen-xl"
                              ? "max-w-screen-xl"
                              : "max-w-screen-2xl"
                        : ""
                    }`
              }`}
            >
              <motion.div
                className={`flex w-full ${
                  position === "top" || position === "bottom"
                    ? useLocalOverlay
                      ? "h-full max-h-full"
                      : "h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)]"
                    : "h-full max-h-full min-h-0"
                } flex-col overflow-hidden rounded-xl bg-white dark:bg-slate-800/80 backdrop-blur-md shadow-2xl dark:shadow-slate-900/90`}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={drawerVariants}
                onAnimationComplete={handleDrawerAnimationComplete}
                data-testid={testId}
              >
                <div className="shrink-0 px-4 py-6 pb-3.5 sm:px-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {title}
                    </div>
                    <div className="flex h-7 shrink-0 items-center gap-2">
                      {headerActions}
                      {!isExpanded && onExpand && (
                        <button
                          type="button"
                          onClick={onExpand}
                          className="cursor-pointer relative rounded-md bg-white dark:bg-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Expand</span>
                          <svg
                            className="size-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                            />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={onClose}
                        className="cursor-pointer relative rounded-md bg-white dark:bg-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <FontAwesomeIcon
                          icon={faXmark}
                          aria-hidden="true"
                          className="size-8"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <motion.div
                  ref={contentRef}
                  className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
                  variants={contentVariants}
                  onAnimationComplete={(definition) => {
                    if (definition === "visible") resetContentScroll();
                  }}
                >
                  {children}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const osDrawerRoot = (
    <AnimatePresence mode="wait">
      {isOpen ? (
        <div className="absolute inset-0 z-[99999]" data-testid="drawer-os-root">
          {drawerContent}
        </div>
      ) : null}
    </AnimatePresence>
  );

  if (isInOsMode) {
    return osPortalTarget ? createPortal(osDrawerRoot, osPortalTarget) : osDrawerRoot;
  }

  if (isContained && containmentTarget) {
    return createPortal(
      <AnimatePresence mode="wait">
        {isOpen ? (
          <div className="absolute inset-0 z-[60]" data-testid="drawer-contained-root">
            {drawerContent}
          </div>
        ) : null}
      </AnimatePresence>,
      containmentTarget,
    );
  }

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <Dialog
          as="div"
          static
          open={isOpen}
          onClose={onClose}
          className="relative z-50"
        >
          {drawerContent}
        </Dialog>
      )}
    </AnimatePresence>
  );
}
